import { LAYOUT, layoutBranchTree, pct, type LayoutNode } from '../../lib/story/branchLayout';
import type { BeatNode } from '../../lib/story/chainTree';
import { TONE_COLORS } from '../../lib/story/ui';

const NODE_H = 20;

/** Where an edge leaves and enters a node (right and left middle). */
const right = (n: LayoutNode) => [n.x + LAYOUT.nodeW, n.y] as const;
const left = (n: LayoutNode) => [n.x, n.y] as const;

function fillOf(n: LayoutNode) {
  if (n.kind === 'outcome') return n.tone ? TONE_COLORS[n.tone] : '#ffffff';
  if (n.kind === 'beat') return '#15121a';
  return '#ffffff';
}

const textFill = (n: LayoutNode) =>
  n.kind === 'beat' || n.tone === 'disaster' || n.tone === 'bad' ? '#ffffff' : '#15121a';

/**
 * The hand-built SVG branch preview (engine.html §5): beats → options → outcomes left to right, each edge labelled with its
 * probability (option edges: the share of the beat's reach; outcome edges: P(outcome | option)), outcomes coloured by tone. Red
 * outlines mark a node over the depth limit, a cycle or a missing reference; a yellow dashed option has no good or windfall path and
 * no tone note. Clicking a node selects it in the outline.
 */
export function BranchPreview({
  root,
  onSelect,
}: {
  root: BeatNode | undefined;
  onSelect?: (n: LayoutNode) => void;
}) {
  const l = layoutBranchTree(root);
  if (!l.nodes.length) return <p className="text-xs text-bruise">No root beat yet.</p>;
  const byId = new Map(l.nodes.map((n) => [n.id, n]));
  return (
    <div className="overflow-x-auto rounded border border-newsprint bg-white">
      <svg
        width={l.width}
        height={l.height}
        viewBox={`0 0 ${l.width} ${l.height}`}
        role="img"
        aria-label="Branch preview"
        className="font-mono"
      >
        {l.edges.map((e, i) => {
          const a = byId.get(e.from);
          const b = byId.get(e.to);
          if (!a || !b) return null;
          const [x1, y1] = right(a);
          const [x2, y2] = left(b);
          const mx = (x1 + x2) / 2;
          return (
            <g key={i} data-testid="branch-edge">
              <path
                d={`M${x1} ${y1} C${mx} ${y1} ${mx} ${y2} ${x2} ${y2}`}
                fill="none"
                stroke="#4b3f5e"
                strokeWidth={1}
              />
              {e.label && (
                <text x={x2 - 3} y={y2 - 3} fontSize={9} textAnchor="end" fill="#4b3f5e">
                  {e.label}
                </text>
              )}
            </g>
          );
        })}
        {l.nodes.map((n) => (
          <g
            key={n.id}
            data-testid={`branch-node-${n.kind}`}
            data-bad={n.bad ? 'true' : undefined}
            data-flag={n.flag ? 'true' : undefined}
            transform={`translate(${n.x} ${n.y - NODE_H / 2})`}
            onClick={() => onSelect?.(n)}
            style={{ cursor: onSelect ? 'pointer' : undefined }}
          >
            <title>
              {`${n.label}${n.tone ? ` (${n.tone})` : ''} · reach ${pct(n.reach)}${n.p !== undefined ? ` · P ${pct(n.p)}` : ''}${n.flag ? ' · no good path and no toneNote' : ''}`}
            </title>
            <rect
              width={LAYOUT.nodeW}
              height={NODE_H}
              rx={n.kind === 'option' ? 10 : 2}
              fill={fillOf(n)}
              stroke={n.bad ? '#e3242b' : n.flag ? '#f3c846' : '#15121a'}
              strokeWidth={n.bad || n.flag ? 2.5 : 1}
              strokeDasharray={n.flag ? '4 2' : undefined}
            />
            <text x={6} y={14} fontSize={10} fill={textFill(n)}>
              {n.label.length > 18 ? `${n.label.slice(0, 17)}…` : n.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}
